import { useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useMeQuery,
  useRefreshPtoProjectSectionsMutation,
  type PtoRefreshSectionsResult,
} from '@shared/api';
import { Button } from '@shared/ui/Button';

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Note = styled.div<{ $ok: boolean }>`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ $ok, theme }) => ($ok ? theme.colors.text.secondary : theme.colors.status.danger)};
`;

const CONFIRM =
  'Обновить разделы и номера строк по всем действующим ревизиям проекта?\n\n' +
  'Программа заново прочитает основные надписи листов (код части РД, номер и наименование листа), ' +
  'обновит графу «Раздел» и пронумерует строки ведомости объёмов. Пока номера не закреплены, они ' +
  'считаются заново. Позиции, количества, решения инженера и привязки Прораба и КСП не меняются.\n\n' +
  'По каждой ревизии ставится задача в очередь ПТО.';

function resultText(res: PtoRefreshSectionsResult): string {
  if (typeof res.queued !== 'number') {
    return 'Задачи поставлены в очередь ПТО. «Раздел» и номера строк обновятся по мере обработки.';
  }
  if (res.queued === 0) return 'Действующих ревизий в проекте нет — обновлять нечего.';
  return `Поставлено задач: ${res.queued} (по одной на действующую ревизию). «Раздел» и номера строк обновятся по мере обработки очереди ПТО.`;
}

/**
 * «Обновить разделы и номера» в реестре РД проекта — только администратору
 * (на сервере `require_admin`): по задаче на каждую действующую ревизию —
 * основные надписи листов → код части и подпись «Раздела» позиций → номера строк.
 * Остальным кнопка не показывается.
 */
export function RefreshSectionsButton({
  projectId,
  projectDeleted,
}: {
  projectId: string;
  projectDeleted: boolean;
}) {
  const { data: me } = useMeQuery();
  const isAdmin = (me?.roles ?? []).includes('admin');
  const [refresh, { isLoading }] = useRefreshPtoProjectSectionsMutation();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  if (!isAdmin) return null;

  async function onRefresh() {
    if (!window.confirm(CONFIRM)) return;
    setNote(null);
    try {
      setNote({ ok: true, text: resultText(await refresh(projectId).unwrap()) });
    } catch (e) {
      setNote({
        ok: false,
        text: apiErrorText(e, 'Не удалось поставить задачи «Обновить разделы и номера».'),
      });
    }
  }

  return (
    <Box>
      <Button
        $variant="secondary"
        $size="sm"
        disabled={isLoading || projectDeleted}
        onClick={() => void onRefresh()}
        title="Администратор: перечитать основные надписи листов действующих ревизий, обновить «Раздел» и номера строк ведомости объёмов"
      >
        {isLoading ? 'Запуск…' : 'Обновить разделы и номера'}
      </Button>
      {note && (
        <Note $ok={note.ok} role="status">
          {note.text}
        </Note>
      )}
    </Box>
  );
}
